import { MatExpansionPanel } from '@angular/material/expansion';
import { MatSnackBar, MatSnackBarRef, TextOnlySnackBar } from '@angular/material/snack-bar';
import { PurchaseReceiptDetail, PurchaseReceiptDetailAllocation } from '../../acumatica/EntityServices/PurchaseReceipt.service';
import { Component, TemplateRef, ViewChild, isDevMode, QueryList, ViewChildren, ElementRef } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { NotFoundError } from '../../acumatica/acumatica-errors';
import { PurchaseReceipt, PurchaseReceiptService } from '../../acumatica/EntityServices/PurchaseReceipt.service';
import { AlertDialog } from '../../common/alert-dialog/alert-dialog.component';
import { PrinterSelectDialog } from '../../common/printer-selector/printer-selector.component';
import { ApplicationScreen } from '../../application-navigator/application-screen';
import { ConfirmationDialog } from '../../common/confirmation-dialog/confirmation-dialog.component';
import { WMSService } from '../../services/wms.service';
import { Observable } from 'rxjs';
import { StockItem, StockItemService } from 'src/app/acumatica/EntityServices/StockItem.service';
import { LoadingIndicator } from 'src/app/acumatica/loading-indicator/AcumaticaLoadingOptions';

const DEFAULT_RECEIVING_PRINTER_STORAGE_KEY: string = 'default-receiving-printer';

@Component({
  selector: 'app-receiving',
  templateUrl: './receiving.component.html',
  styleUrls: ['./receiving.component.css']
})
export class ReceivingComponent extends ApplicationScreen {

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private snackbar: MatSnackBar,
    private receipts: PurchaseReceiptService,
    private stockItems: StockItemService,
    private wms: WMSService
  ) {
    super();

    // get the shipment number from the navigation router
    this.route.paramMap.subscribe(params => {
      this.receiptNbr = params.get('receiptNbr');
    });
  }

  pageTitle = new FormControl('Receiving');
  allocationsDisplayColumns = ['LotSerialNbr', 'Qty', 'PrimaryAction'];

  // #region Receipt Number & data retrieval
  @ViewChildren("receiptDetailPanel", { read: ElementRef }) detailPanelElems!: QueryList<ElementRef>;
  @ViewChildren("receiptDetailPanel") detailPanels!: QueryList<MatExpansionPanel>;
  getDetailPanel(detail: PurchaseReceiptDetail): MatExpansionPanel | null {
    const panelIndex = this.detailPanelElems.toArray().findIndex(e => e.nativeElement.getAttribute('data-linenbr') == detail.LineNbr);
    return panelIndex == -1 ? null : this.detailPanels.toArray()[panelIndex];
  }

  private _receiptNbr: string | null = null;
  get receiptNbr() { return this._receiptNbr; }
  set receiptNbr(value) {
    this._receiptNbr = value;
    if (!value) { this.router.navigate(['/receiving']); return; }

    this.receipts.get('Receipt', value, {
      expand: ['Details', 'Details/Allocations'],
    }).subscribe(receipt => {
      // check Receipt Status
      if (receipt.Status != 'Open' && receipt.Status != 'Balanced') {
        this.dialog.open(AlertDialog, {
          data: { icon: 'error', message: "Receipt cannot be processed\nStatus is " + receipt.Status, buttonText: 'Ok' }
        });
        this.router.navigate(['/receiving']);
        return;
      }

      this.isDirty = true;
      this.pageTitle.setValue("Receipt: " + receipt.ReceiptNbr);
      this.receipt = receipt;
    },
      (error: any) => {
        console.error(error);
        if (error instanceof NotFoundError) error.message = "Not Found - Invalid Receipt Number";
        this.dialog.open(AlertDialog, {
          data: { icon: 'error', message: error.message, buttonText: 'Ok' }
        });
        this.isDirty = false;
        this.router.navigate(['/receiving']);
      });
  }

  private _receipt: PurchaseReceipt | null = null;
  get receipt() { return this._receipt; }
  set receipt(value: PurchaseReceipt | null) {
    this._receipt = value;
    if (value == null) return;

    // fetch Stock Items (sequentially) and associate with Detail lines (possibly multiple lines for same item -- fetch item once)
    let index = -1;
    let getters: { [inventoryID: string]: Observable<StockItem> } = {};
    const getNextItem = () => {
      if (++index >= value.Details!.length) return;

      const detail = value.Details![index];
      const inventoryID = detail.InventoryID;
      if (getters[inventoryID] === undefined) {
        getters[inventoryID] = this.stockItems.get(inventoryID, { loader: { indicator: LoadingIndicator.snackbar, message: `Getting item ${inventoryID}` } });
        getters[inventoryID].subscribe(() => getNextItem());
      }
      getters[inventoryID].subscribe(item => detail.item = item);
    }
    getNextItem();
  }
  // #endregion

  // #region Search form
  searchForm = new FormGroup({ search: new FormControl() });
  get searchValue(): string { return (this.searchForm.get('search')!.value || '').toUpperCase(); }
  set searchValue(value) { this.searchForm.get('search')!.setValue(value); }
  searchReset = () => {
    let ctl = this.searchForm.get('search')!;
    ctl.setValue(null);
    setTimeout(() => { (<any>ctl).focus(); });
  }

  performSearch(event: Event): void {
    let search = this.searchValue;

    // look for matching Inventory ID or Vendor ID #
    let inventoryIDMatches = this.receipt?.Details?.filter(detail =>
      !detail.allPiecesReceived() &&
      (detail.InventoryID == search || detail.AlternateID == search)
    ) || [];
    if (inventoryIDMatches.length > 0) {
      const panel = this.getDetailPanel(inventoryIDMatches[0]);
      panel?.open();
      this.searchValue = '';
      return;
    }

    // look for matching Vendor ID #s

    enum SearchResultType {
      LotNbr,
      VendorNbr,
      InventoryID
    }
    interface SearchResult {
      type: SearchResultType,
      detail: PurchaseReceiptDetail,
      allocation: PurchaseReceiptDetailAllocation
    }

    var results: SearchResult[] = [];

    // look for matching Lot #
    results = results.concat(
      // map the Details array contents
      this.receipt?.Details?.map(detail =>
        detail.Allocations
          // filtering allocations to those that match the Lot / Serial #
          .filter(alloc => alloc.LotSerialNbr == search)
          // converting them into a SearchResult
          .map(alloc => <SearchResult>{ type: SearchResultType.LotNbr, detail: detail, allocation: alloc })
        // flatten the Details-array of SearchResults-array into a single SearchResults array
      ).flat() || []
    );

    switch (results.length) {
      case 0:
        this.dialog.open(AlertDialog, { data: { message: "No matching Item Numbers or\nLot Numbers were found", buttonText: "OK" } })
          .afterClosed().subscribe(() => {
            (<any>this.searchForm.get('search')!).focus();
          });
        break;

      case 1:
        let result = results[0];
        this.receivedPiece(result.detail, result.allocation).subscribe(() => {
          // need to wait for Label print dialog to close to set focus
          this.searchReset();
        });

        this.snackbar.open(
          "Received: " + result.detail.item.Description + "\n" +
          result.detail.InventoryID + " / " + result.allocation.LotSerialNbr + " x" + result.allocation.Qty + " " + result.detail.UOM,
          undefined,
          { duration: 5000, panelClass: 'pre-wrap' }
        );
        break;

      default:
        // TODO: provide Allocation selection via dialog
        this.dialog.open(AlertDialog, { data: { message: "Multiple search matches were found\nSelect details to receive directly" } });
        this.searchReset();
    }
  }
  // #endregion

  // #region Line Detail handlers
  receivedAllPieces(detail: PurchaseReceiptDetail) {
    if (detail.allPiecesReceived()) return;

    // dialog confirmation for all pieces received
    this.dialog.open(ConfirmationDialog, {
      data: {
        message: 'Confirm receipt of all pieces for this line\n',
        confirmText: detail.Allocations.length.toString() + ' pieces'
      }
    }).afterClosed().subscribe(answer => {
      // confirmed by user, iterate each piece
      detail.Allocations.forEach(alloc => {
        if (!alloc.received) this.receivedPiece(detail, alloc).subscribe();
      });
    })
  }

  receivedPiece(detail: PurchaseReceiptDetail, alloc: PurchaseReceiptDetailAllocation): Observable<void> {
    return new Observable<void>(subscriber => {
      // was the item received previously (set true) then unreceived (set false)
      // alloc.received is undefined if not previously received
      let wasUnreceived = alloc.received === false;
      // mark as now received
      alloc.received = true;

      if (wasUnreceived) {
        // ask user if reprint label
        this.dialog.open(ConfirmationDialog, {
          data: {
            message: 'Re-print label for piece?\nItem: ' + detail.InventoryID + '\nLot #: ' + alloc.LotSerialNbr,
            confirmText: 'Yes',
            cancelText: 'No'
          }
        }).afterClosed().subscribe(answer => {
          if (answer === true) this.printStockTag(detail, alloc).subscribe(subscriber);
        })
      } else {
        // first time received, no prompt necessary for label
        this.printStockTag(detail, alloc).subscribe(subscriber);
      }
    })
  }

  unreceivePiece(detail: PurchaseReceiptDetail, alloc: PurchaseReceiptDetailAllocation) {
    alloc.received = false;
  }

  @ViewChild('editReceiptDetailDialog') editReceiptDetailDialog!: TemplateRef<any>;
  editPieceForm = new FormGroup({
    SerLotNbr: new FormControl(),
    Qty: new FormControl()
  });
  get editPieceLotNbr(): string | null { return this.editPieceForm.get('SerLotNbr')?.value; }
  set editPieceLotNbr(value: string | null) { this.editPieceForm.get('SerLotNbr')?.setValue(value); }
  get editPieceQty(): number | null { return this.editPieceForm.get('Qty')?.value; }
  set editPieceQty(value: number | null) { this.editPieceForm.get('Qty')?.setValue(value); }
  editPieceDetails(detail: PurchaseReceiptDetail, alloc: PurchaseReceiptDetailAllocation) {
    this.editPieceLotNbr = alloc.LotSerialNbr;
    this.editPieceQty = alloc.Qty;
    this.dialog.open(this.editReceiptDetailDialog, {
      data: {
        ReceiptLine: detail,
        Allocation: alloc
      }
    }).afterClosed().subscribe(confirmed => {
      if (confirmed) {
        alloc.LotSerialNbr = this.editPieceLotNbr;
        alloc.Qty = this.editPieceQty!;
      }
    })
  }
  // #endregion

  // #region Labels / Printing

  get DefaultReceivingPrinter(): string | null { return localStorage.getItem(DEFAULT_RECEIVING_PRINTER_STORAGE_KEY); }
  set DefaultReceivingPrinter(value: string | null) {
    if (value) localStorage.setItem(DEFAULT_RECEIVING_PRINTER_STORAGE_KEY, value);
    else localStorage.removeItem(DEFAULT_RECEIVING_PRINTER_STORAGE_KEY);
  }

  pickLabelPrinter(): Observable<void> {
    return new Observable<void>(subscriber => {
      this.dialog.open(PrinterSelectDialog, {
        data: {
          PrinterID: this.DefaultReceivingPrinter,
          ConfirmButtonText: 'OK'
        }
      }).afterClosed().subscribe(result => {
        if (!result?.Printer) return;
        this.DefaultReceivingPrinter = result.Printer.id;
        subscriber.next();
      });
    });
  }

  printStockTag(detail: PurchaseReceiptDetail, alloc: PurchaseReceiptDetailAllocation): Observable<void> {
    return new Observable<void>(subscriber => {
      let printTag = () => {
        this.wms.printStockRollTag(
          this.DefaultReceivingPrinter!,
          detail.InventoryID,
          alloc.LotSerialNbr!,
          alloc.Qty,
          this.receipt!.ReceiptNbr,
          this.receipt!.VendorID
        ).subscribe(subscriber);
      }

      if (this.DefaultReceivingPrinter == null) this.pickLabelPrinter().subscribe(printTag);
      else printTag();
    })
  }
  // #endregion

  releaseReceipt(): void {
    let allReceived = this.receipt!.allLinesReceived();

    this.dialog.open(ConfirmationDialog, {
      data: {
        icon: allReceived
          ? 'install_desktop'
          : 'info',
        message: allReceived
          ? "Release Purchase Receipt?"
          : "All lines have not been fully received!\nRelease Partial Receipt?",
        confirmText: 'Yes'
      }
    }).afterClosed().subscribe(answer => {
      if (answer) {
        this.receipts.put(this.receipt!, { expand: 'Details' }).subscribe(updatedReceipt => {
          this.receipts.Release2(updatedReceipt).subscribe(() => {
            this.isDirty = false;
            this.router.navigate(['/receiving']);
          });
        })
      }
    });
  }
}
